<template>
  <v-layout>
    <v-flex>
      <v-card>
        <v-card-title>
          {{ $t('keystoreList') }}
          <v-spacer></v-spacer>
          <v-text-field id="b3e8e68d" v-model="search" :label="$t('search')" append-icon="mdi-magnify" single-line hide-details></v-text-field>
        </v-card-title>
        <v-data-table
          id="5db4aa6f"
          must-sort
          :search="search"
          :headers="headers"
          :items="keystoreList"
          :options.sync="pagination"
          :server-items-length="totalItems"
          :loading="loading"
          :loading-text="$t('dataTable.loadingPleaseWait')"
          :no-data-text="$t('dataTable.noDataAvailable')"
          :no-results-text="$t('dataTable.noMatchingRecordsFound')"
          :footer-props="{
            'items-per-page-text': $t('dataFooter.itemsPerPage'),
            'page-text': $t('dataFooter.pageText'),
            'items-per-page-options': [10,25,50]
          }"
        >
          <template v-slot:item.activated="{ item }">
            <v-btn
              :id="setTableItemOperationDynamicId('141a198b', item.name)"
              min-width="140"
              :disabled="itemDisableInvestigation(item.activated)"
              :color="item.activated ? 'green' : 'error'"
              @click="item.activated ? deactivateKeyStore(item.name) : showActivationDialog(item)"
            >
              {{ item.activated ? $t('activated') : $t('deactivated') }}
            </v-btn>
          </template>

          <template v-slot:item.autoActivation="{ item }">
            <span>{{ item.autoActivation ? $t('on').toUpperCase() : $t('off').toUpperCase() }}</span>
          </template>

          <template v-if="isAdmin" v-slot:item.action="{ item }">
            <v-tooltip right>
              <template v-slot:activator="{ on }">
                <v-btn :id="setTableItemOperationDynamicId('cab33724', item.name)" icon v-on="on" @click="deleteKeystore(item.name)">
                  <v-icon>mdi-delete</v-icon>
                </v-btn>
              </template>
              <span>{{ $t('delete') }}</span>
            </v-tooltip>
          </template>
        </v-data-table>
      </v-card>

      <fab-button v-if="isAdmin" id="21351a25" tooltip-text="add" to="keystores/add" />

      <!-- TODO ezt bele epiteni az App.vue confirmationDialog-be (egyben legyen) -->
      <!-- confirmationDialog -->
      <v-row justify="center">
        <v-dialog v-model="activationDialog" persistent max-width="450">
          <v-card id="a717397d">
            <v-card-title class="headline yellow darken-3">
              <v-icon id="c66e87f6" left large>mdi-alert-circle</v-icon>
              {{ $t('activation') }}
            </v-card-title>
            <v-form ref="form" v-model="activationDialogValid">
              <v-card-text>
                <v-container>
                  <v-text-field
                    id="06c32188"
                    v-model="password"
                    :label="$t('password')+`*`"
                    type="password"
                    :rules="[rules.required]"
                    required
                  ></v-text-field>
                  <v-switch id="460aeb2f" v-model="autoActivation" :label="$t('autoActivation')"></v-switch>
                  <small>*{{ $t('indicatesRequiredField') }}</small>
                </v-container>
              </v-card-text>
            </v-form>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="blue darken-3" text @click="activationDialog = false">{{ $t('close') }}</v-btn>
              <v-btn id="111b8f65" class="primary" @click="activateKeyStore()">{{ $t('activation') }}</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-row>
    </v-flex>
  </v-layout>
</template>

<script>
import keystores from '@/api/keystores'
import debounce from 'lodash.debounce'
import Role from '../../Role'
import { showToastSuccess, showToastError, showConfirmationDialog } from '../../event-bus'
import { mapGetters } from 'vuex'
import FabButton from '../../components/FabButton.vue'

export default {
  name: 'KeyStoreList',

  components: {
    'fab-button': FabButton
  },

  data() {
    return {
      search: null,
      pagination: {
        sortBy: ['name']
      },
      keystoreList: [],
      totalItems: 0,
      loading: false,
      headers: [
        {
          text: this.$t('name'),
          align: 'left',
          value: 'name'
        },
        {
          text: this.$t('type'),
          value: 'type',
          sortable: false
        },
        {
          text: this.$t('activated'),
          value: 'activated',
          sortable: false
        },
        {
          text: this.$t('autoActivation'),
          value: 'autoActivation',
          sortable: false
        },
        {
          text: this.$t('actions'),
          value: 'action',
          sortable: false
        }
      ],
      activationDialog: false,
      activationDialogValid: true,
      name: null,
      password: null,
      autoActivation: null,
      rules: {
        required: v => !!v || this.$t('validation.requiredField')
      }
    }
  },

  computed: {
    ...mapGetters(['hasRole']),
    isAdmin() {
      return this.hasRole(Role.ADMIN)
    },
    isOperator() {
      return this.hasRole(Role.OPERATOR)
    }
  },

  watch: {
    search() {
      this.loading = true
      this.searchKeystoreWatcher()
    },

    pagination() {
      this.searchKeystores()
    }
  },

  methods: {
    async searchKeystores() {
      this.loading = true
      try {
        let pageRequest = {
          'page-num': this.pagination.page,
          'page-size': this.pagination.itemsPerPage,
          'sort-field': this.pagination.sortBy[0],
          'sort-order': this.pagination.sortDesc[0] ? 'desc' : 'asc'
        }
        let page = await keystores.listKeyStores(this.search, pageRequest)
        this.keystoreList = page.content
        this.totalItems = page.total
      } catch (error) {
        showToastError(this.$t('toastMessage.errorWhileRetrievingData'))
      } finally {
        this.loading = false
      }
    },

    searchKeystoreWatcher: debounce(function() {
      this.searchKeystores()
    }, 500),

    async deleteKeystore(name) {
      let conf = await showConfirmationDialog(this.$t('confirmation'), this.$t('dialog.text.deleteItem'))
      if (!conf.confirmedValue) {
        return
      }

      try {
        await keystores.deleteKeyStore(name)
        this.searchKeystores()
        showToastSuccess(this.$t('toastMessage.succesfullyDeleted'))
      } catch (error) {
        let errorMsg = this.$t('toastMessage.errorWhileRetrievingData')
        if (error.response != null) {
          switch (error.response.data.code) {
            case 'STILL_REFERENCED':
              errorMsg = this.$t('toastMessage.stillReferenced', { param: 'KeyStore', param2: error.response.data.message.split(' ')[5] })
              break
            case 'GENERAL':
              errorMsg = '(' + error.response.data.code + ')' + ' ' + error.response.data.message
          }
        }
        showToastError(errorMsg)
      }
    },

    showActivationDialog(item) {
      this.activationDialog = true
      this.$nextTick(() => {
        this.$refs.form.reset()
        this.name = item.name
        this.autoActivation = item.autoActivation
      })
    },

    async activateKeyStore() {
      if (!this.$refs.form.validate()) {
        return
      }
      this.activationDialog = false

      let keyStoreActivationRequest = {
        password: this.password,
        autoActivation: this.autoActivation
      }
      try {
        await keystores.activateKeyStore(this.name, keyStoreActivationRequest)
        this.searchKeystores()
        showToastSuccess(this.$t('toastMessage.succesfullyActivated'))
      } catch (error) {
        let errorMsg = this.$t('toastMessage.errorWhileRetrievingData')
        if (error.response != null) {
          switch (error.response.data.code) {
            case 'GENERAL':
              errorMsg = '(' + error.response.data.code + ')' + ' ' + error.response.data.message
          }
        }
        showToastError(errorMsg)
      }
    },

    async deactivateKeyStore(name) {
      let conf = await showConfirmationDialog(this.$t('deactivating'), this.$t('dialog.text.deactivateItem'))
      if (!conf.confirmedValue) {
        return
      }

      try {
        await keystores.deactivateKeyStore(name)
        this.searchKeystores()
        showToastSuccess(this.$t('toastMessage.succesfullyDeactivated'))
      } catch (error) {
        let errorMsg = this.$t('toastMessage.errorWhileRetrievingData')
        if (error.response != null) {
          switch (error.response.data.code) {
            case 'GENERAL':
              errorMsg = '(' + error.response.data.code + ')' + ' ' + error.response.data.message
          }
        }
        showToastError(errorMsg)
      }
    },

    itemDisableInvestigation(isItemActivated) {
      let val = true
      if (this.isAdmin) {
        val = false
      } else if (this.isOperator) {
        if (!isItemActivated) {
          val = false
        }
      }
      return val
    }
  }
}
</script>
